import { useState, useEffect, Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import LoadingScreen from './components/LoadingScreen'
import ImpersonationBanner from './components/ImpersonationBanner'

// Eager imports (critical path)
import SignIn from './pages/SignIn'
import Dashboard from './pages/Dashboard'
import { DashboardLayout } from './components/layout/DashboardLayout'
import { AdminLayout } from './components/layout/AdminLayout'

// Lazy: Auth pages
const SignUp = lazy(() => import('./pages/SignUp'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const SetupPassword = lazy(() => import('./pages/SetupPassword'))

// Lazy: Documentation
const Documentation = lazy(() => import('./pages/Documentation'))
const PublicDocs = lazy(() => import('./pages/PublicDocs'))

// Lazy: Learner pages
const Courses = lazy(() => import('./pages/Courses'))
const CoursePreProcess = lazy(() => import('./pages/CoursePreProcess'))
const AssessmentQuestion = lazy(() => import('./pages/AssessmentQuestion'))
const AssessmentReview = lazy(() => import('./pages/AssessmentReview'))
const FirstAssessmentReview = lazy(() => import('./pages/FirstAssessmentReview'))
const ReassessmentQuestion = lazy(() => import('./pages/ReassessmentQuestion'))
const ReassessmentReview = lazy(() => import('./pages/ReassessmentReview'))
const AssessmentLearn = lazy(() => import('./pages/AssessmentLearn'))
const AssessmentComplete = lazy(() => import('./pages/AssessmentComplete'))
const Learners = lazy(() => import('./pages/Learners'))
const Settings = lazy(() => import('./pages/Settings'))

// Lazy: Admin pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const CourseList = lazy(() => import('./pages/admin/CourseList'))
const CourseDetail = lazy(() => import('./pages/admin/CourseDetail'))
const CreateCourse = lazy(() => import('./pages/admin/CreateCourse'))
const EditCourse = lazy(() => import('./pages/admin/EditCourse'))
const AddQuestions = lazy(() => import('./pages/admin/AddQuestions'))
const CourseAssignments = lazy(() => import('./pages/admin/CourseAssignments'))
const QuestionBank = lazy(() => import('./pages/admin/QuestionBank'))
const Reports = lazy(() => import('./pages/admin/Reports'))
const PredictiveError = lazy(() => import('./pages/admin/reports/PredictiveError'))
const CourseSummary = lazy(() => import('./pages/admin/reports/CourseSummary'))
const StruggleReport = lazy(() => import('./pages/admin/reports/StruggleReport'))
const TimeToLearn = lazy(() => import('./pages/admin/reports/TimeToLearn'))
const LearnerProfile = lazy(() => import('./pages/admin/LearnerProfile'))
const CompanySettings = lazy(() => import('./pages/admin/CompanySettings'))
const AdminProfile = lazy(() => import('./pages/admin/AdminProfile'))

// Protected Route Component
function ProtectedRoute({ children, requiredRole }) {
  const { session, loading, user, authError } = useAuth()

  // Show loading while auth is initializing
  if (loading) {
    return <LoadingScreen />
  }

  // No session means not authenticated
  if (!session) {
    return <Navigate to="/signin" replace />
  }

  // Session exists but profile fetch failed - show error instead of redirect loop
  if (authError && !user) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '2rem', textAlign: 'center' }}>
        <h2>Something went wrong</h2>
        <p style={{ color: '#666', marginBottom: '1rem' }}>{authError}</p>
        <button onClick={() => window.location.reload()} style={{ padding: '0.5rem 1.5rem', cursor: 'pointer', borderRadius: '4px', border: '1px solid #ccc', background: '#fff' }}>
          Try Again
        </button>
      </div>
    )
  }

  // Session exists but user profile not loaded yet
  if (!user) {
    return <LoadingScreen />
  }

  // Check role requirements
  if (requiredRole && user.role !== requiredRole) {
    // Redirect to appropriate dashboard based on actual role
    const redirectPath = user.role === 'admin' ? '/admin/dashboard' : '/dashboard'
    return <Navigate to={redirectPath} replace />
  }

  return children
}

// Root redirect component - checks role and redirects to appropriate dashboard
function RootRedirect() {
  const { session, loading, user } = useAuth()

  // Add timeout to prevent infinite loading on mobile
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimedOut(true)
    }, 3000) // 3 second timeout

    return () => clearTimeout(timer)
  }, [])

  // If timed out and still loading, assume not authenticated
  if (timedOut && loading) {
    return <Navigate to="/signin" replace />
  }

  // Show loading during auth initialization (with timeout)
  if (loading && !timedOut) {
    return <LoadingScreen />
  }

  // Not authenticated
  if (!session) {
    return <Navigate to="/signin" replace />
  }

  // Wait for user profile (with timeout)
  if (!user && !timedOut) {
    return <LoadingScreen />
  }

  // If user still not loaded after timeout, go to signin
  if (!user && timedOut) {
    return <Navigate to="/signin" replace />
  }

  // Redirect based on user role
  const redirectPath = user.role === 'admin' ? '/admin/dashboard' : '/dashboard'
  return <Navigate to={redirectPath} replace />
}

function App() {
  return (
    <Router>
      <AuthProvider>
        {/* Impersonation banner - shown at top when admin is viewing as learner */}
        <ImpersonationBanner />

        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Public routes */}
            <Route path="/signin" element={<SignIn />} />
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/sign-up" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/setup-password" element={<SetupPassword />} />

            {/* Public documentation routes */}
            <Route path="/docs" element={<PublicDocs />} />
            <Route path="/docs/:docId" element={<PublicDocs />} />

            {/* Help landing page - protected but no layout */}
            <Route path="/help" element={<ProtectedRoute><Documentation /></ProtectedRoute>} />

            {/* User Protected routes - wrapped in Dashboard Layout */}
            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:courseId" element={<CoursePreProcess />} />
              <Route path="/assessment/:courseId/question/:questionId" element={<AssessmentQuestion />} />
              <Route path="/assessment/:courseId/review" element={<FirstAssessmentReview />} />
              <Route path="/assessment/:courseId/learn" element={<AssessmentLearn />} />
              <Route path="/assessment/:courseId/reassess/:questionId" element={<ReassessmentQuestion />} />
              <Route path="/assessment/:courseId/reassess-review" element={<ReassessmentReview />} />
              <Route path="/assessment/:courseId/complete" element={<AssessmentComplete />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/help/:categoryId/:docId" element={<Documentation />} />
            </Route>

            {/* Admin Protected routes - wrapped in Admin Layout */}
            <Route element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/courses" element={<CourseList />} />
              <Route path="/admin/courses/create" element={<CreateCourse />} />
              <Route path="/admin/courses/:id" element={<CourseDetail />} />
              <Route path="/admin/courses/:id/edit" element={<EditCourse />} />
              <Route path="/admin/courses/:id/questions" element={<AddQuestions />} />
              <Route path="/admin/courses/:id/assignments" element={<CourseAssignments />} />
              <Route path="/admin/learners" element={<Learners />} />
              <Route path="/admin/learners/:id" element={<LearnerProfile />} />
              <Route path="/admin/questions" element={<QuestionBank />} />
              <Route path="/admin/reports" element={<Reports />} />
              <Route path="/admin/reports/predictive-error" element={<PredictiveError />} />
              <Route path="/admin/reports/course-summary" element={<CourseSummary />} />
              <Route path="/admin/reports/struggle" element={<StruggleReport />} />
              <Route path="/admin/reports/time-to-learn" element={<TimeToLearn />} />
              <Route path="/admin/settings" element={<CompanySettings />} />
              <Route path="/admin/profile" element={<AdminProfile />} />
            </Route>

            {/* Default route - redirect based on role */}
            <Route path="/" element={<RootRedirect />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </Router>
  )
}

export default App
